/**
 * filterFields — shared filter field configuration.
 *
 * Single source of truth for the set of fields that can be used to build a
 * filter condition against items. Consumed by:
 *   - FilterPanel.tsx (item list page's compound filter builder)
 *
 * This module intentionally has NO dependency on Zustand stores or React —
 * it is a plain data module so it can be imported from anywhere. Fields whose
 * option list depends on workspace data (queue, cycle, track, session,
 * collection, package/cluster/exclusive groups, custom fields) are marked
 * `dynamicOptions: true` (or, for custom fields, produced by
 * `buildCustomFieldsGroup`) — callers inject real options via
 * `resolveFilterFieldGroups()`, built from their own store hooks.
 */

import type { Attribute } from '../../../../types/prioritization';
import {
  ITEM_FIELDS,
  FILTER_GROUP_ORDER,
  type FilterFieldType,
  type FilterFieldOption,
} from './itemFields';

// ============================================================================
// Types
// ============================================================================
//
// The atomic filter types (`FilterFieldType`, `FilterFieldOption`) are owned by
// `itemFields.ts` and re-exported here so existing importers of this module keep
// working unchanged.

export type { FilterFieldType, FilterFieldOption } from './itemFields';

export interface FilterFieldDef {
  field: string;
  label: string;
  type: FilterFieldType;
  description?: string;
  options?: FilterFieldOption[];
  /** When true, `options` must be injected at render time from workspace data. */
  dynamicOptions?: boolean;
}

export interface FilterFieldGroup {
  label: string;
  fields: FilterFieldDef[];
}

/** Options for fields flagged `dynamicOptions: true`, keyed by field id. */
export type DynamicOptionsMap = Record<string, FilterFieldOption[]>;

// ============================================================================
// Static field groups — DERIVED from the itemFields registry
// ============================================================================
//
// Built by grouping every registry field that has a `filter` facet under
// FILTER_GROUP_ORDER, in registry order within each group. Adding a filterable
// field is a single entry in itemFields.ts — it appears here automatically.

export const FILTER_FIELD_GROUPS: FilterFieldGroup[] = FILTER_GROUP_ORDER.map((groupLabel) => ({
  label: groupLabel,
  fields: ITEM_FIELDS.filter((f) => f.filter && f.filter.group === groupLabel).map((f) => {
    const flt = f.filter!;
    const def: FilterFieldDef = { field: f.key, label: flt.label, type: flt.type };
    if (flt.description !== undefined) def.description = flt.description;
    if (flt.options !== undefined) def.options = flt.options;
    if (flt.dynamicOptions !== undefined) def.dynamicOptions = flt.dynamicOptions;
    return def;
  }),
}));

/** Flat list of all static fields (excludes the dynamically-built custom fields group). */
export const ALL_FILTER_FIELDS: FilterFieldDef[] = FILTER_FIELD_GROUPS.flatMap((g) => g.fields);

// ============================================================================
// Custom fields (workspace attributes) — dynamic group
// ============================================================================

/**
 * Builds the "Custom Fields" filter group from workspace custom-field
 * attributes (Attribute.isCustomField === true). Field id convention:
 * `meta:${attribute.key}` — resolved by `getFieldValue()` in
 * useItemListData.ts, which reads `item.metadata?.[attribute.key]`.
 *
 * Type mapping (per attribute.type):
 *   text                  → 'text'  (operator 'eq'/'text')
 *   select | multiselect   → 'set'   (operator 'in', options from attribute.options)
 *   number                → 'range' (operator 'range')
 *   boolean               → 'boolean'
 *   date                  → 'date-range'
 */
export function buildCustomFieldsGroup(customFields: Attribute[]): FilterFieldGroup | null {
  if (!customFields || customFields.length === 0) return null;

  const fields: FilterFieldDef[] = customFields.map((attr) => {
    const field = `meta:${attr.key}`;
    switch (attr.type) {
      case 'select':
      case 'multiselect':
        return {
          field,
          label: attr.label,
          type: 'set',
          description: attr.description,
          options: (attr.options ?? []).map((opt) => ({ value: opt, label: opt })),
        };
      case 'number':
        return { field, label: attr.label, type: 'range', description: attr.description };
      case 'boolean':
        return { field, label: attr.label, type: 'boolean', description: attr.description };
      case 'date':
        return { field, label: attr.label, type: 'date-range', description: attr.description };
      case 'text':
      default:
        return { field, label: attr.label, type: 'text', description: attr.description };
    }
  });

  return { label: 'Custom Fields', fields };
}

// ============================================================================
// Resolution — merge static groups + dynamic options + custom fields
// ============================================================================

export interface ResolveFilterFieldGroupsParams {
  /** Options for fields flagged `dynamicOptions: true` (queue, cycleId, trackId, sessionId, inCollection, inPackage, inCluster, inExclusive). */
  dynamicOptionsMap?: DynamicOptionsMap;
  /** Enabled workspace custom-field attributes (objectType 'item'); rendered as a trailing "Custom Fields" group. */
  customFields?: Attribute[];
  /** Feature-flag gate for the "Subject type" (metaCategory) field. Defaults to true. */
  includeMetaCategory?: boolean;
}

/**
 * Returns the full, render-ready list of filter field groups: static groups
 * with dynamic options injected, empty dynamic groups (Queue/Activity) hidden
 * when no data is loaded, plus a trailing Custom Fields group when the
 * workspace has any enabled custom fields.
 *
 * FilterPanel.tsx calls this and builds its `dynamicOptionsMap` from its own
 * store hooks (this module never imports stores directly), so a follow-up
 * field group (e.g. creationProvenance) is added here once.
 */
export function resolveFilterFieldGroups(params: ResolveFilterFieldGroupsParams): FilterFieldGroup[] {
  const { dynamicOptionsMap = {}, customFields = [], includeMetaCategory = true } = params;

  const groups = FILTER_FIELD_GROUPS
    .map((group) => ({
      ...group,
      fields: group.fields
        .filter((f) => f.field !== 'metaCategory' || includeMetaCategory)
        .map((f) => (f.dynamicOptions ? { ...f, options: dynamicOptionsMap[f.field] ?? [] } : f)),
    }))
    .filter((group) => {
      // Hide dynamic groups entirely when no data has loaded yet (matches prior FilterPanel behavior)
      if (group.label === 'Queue') return (dynamicOptionsMap.queue?.length ?? 0) > 0;
      if (group.label === 'Activity') {
        return (
          (dynamicOptionsMap.cycleId?.length ?? 0) > 0 ||
          (dynamicOptionsMap.trackId?.length ?? 0) > 0 ||
          (dynamicOptionsMap.sessionId?.length ?? 0) > 0
        );
      }
      return true;
    });

  const customGroup = buildCustomFieldsGroup(customFields);
  return customGroup ? [...groups, customGroup] : groups;
}

/** Flattens resolved groups for lookups like "find the FilterFieldDef for this selected field id". */
export function flattenFilterFieldGroups(groups: FilterFieldGroup[]): FilterFieldDef[] {
  return groups.flatMap((g) => g.fields);
}
